import type { DefaultRuleFlow, DefaultRuleFlowTemplate } from "./types";

/**
 * Field-binding primitives for default-rule flow templates.
 *
 * Each default rule declares its flow as a mostly-literal object, but
 * wherever a value comes from the rule's `configurations` blob, a
 * `FieldBinding` marker is used instead. Shared walkers then:
 *
 *   - `resolveTemplate` — substitute bindings with `config[path]` (or
 *      the binding's `default`) to produce a concrete `DefaultRuleFlow`.
 *   - `extractConfig` — walk the template alongside an edited flow, and
 *     for each binding read the value at the same position in the
 *     edited flow back into a fresh config object.
 *   - `collectEditableFlowPaths` — produce the set of canonical
 *     step-id-rooted paths where the edit UI should permit editing.
 *
 * Bindings target a single top-level key on the rule's `configurations`
 * object; nested paths are not supported.
 */

const FIELD_BINDING_MARKER = "__fieldBinding" as const;

export interface FieldBinding<T> {
  readonly __fieldBinding: true;
  /** Top-level key on the rule's `configurations` object. */
  readonly path: string;
  /** Value used when the config has no entry at `path`. */
  readonly default: T;
}

export function isFieldBinding(v: unknown): v is FieldBinding<unknown> {
  return (
    typeof v === "object" &&
    v !== null &&
    (v as Record<string, unknown>)[FIELD_BINDING_MARKER] === true
  );
}

/**
 * Returns a typed `bind` function scoped to a specific config type so
 * the `path` literal is checked against the config's keys at compile
 * time.
 */
export function makeBind<TConfig>(): <K extends keyof TConfig & string>(
  path: K,
  default_: TConfig[K],
) => FieldBinding<TConfig[K]> {
  return <K extends keyof TConfig & string>(path: K, default_: TConfig[K]) => ({
    __fieldBinding: true as const,
    path,
    default: default_,
  });
}

// ──────────────────────────────────────────────────────────────────────
// Walkers
// ──────────────────────────────────────────────────────────────────────

const UNSAFE_KEYS = new Set(["__proto__", "constructor", "prototype"]);

/**
 * Recursively substitutes every `FieldBinding` in `template` with
 * `config[binding.path]`, falling back to the binding's `default` when
 * the config has nothing at that path. Pure data walk — arrays and
 * plain objects are copied, everything else is passed through.
 */
export function resolveTemplate<TConfig>(
  template: DefaultRuleFlowTemplate,
  config: TConfig,
): DefaultRuleFlow;
export function resolveTemplate<TConfig>(
  template: unknown,
  config: TConfig,
): unknown;
export function resolveTemplate<TConfig>(
  template: unknown,
  config: TConfig,
): unknown {
  if (isFieldBinding(template)) {
    if (
      config == null ||
      typeof config !== "object" ||
      UNSAFE_KEYS.has(template.path)
    ) {
      return template.default;
    }
    const value = (config as Record<string, unknown>)[template.path];
    return value !== undefined ? value : template.default;
  }
  if (Array.isArray(template)) {
    return template.map((item) => resolveTemplate(item, config));
  }
  if (template !== null && typeof template === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(template as Record<string, unknown>)) {
      out[k] = resolveTemplate(v, config);
    }
    return out;
  }
  return template;
}

/**
 * Walks `template` and `editedFlow` in parallel; every `FieldBinding`
 * encountered in the template sources the corresponding leaf value
 * from the edited flow and writes it into a fresh config object at
 * `binding.path`.
 *
 * Assumes the edited flow has the same structural shape as the
 * template (same object keys, same array lengths). The edit UI enforces
 * this because `structureLocked` disallows add/delete.
 */
export function extractConfig<TConfig>(
  template: unknown,
  editedFlow: unknown,
): TConfig {
  const config: Record<string, unknown> = Object.create(null);
  walkExtract(template, editedFlow, config);
  return config as TConfig;
}

function walkExtract(
  template: unknown,
  edited: unknown,
  config: Record<string, unknown>,
): void {
  if (isFieldBinding(template)) {
    if (!UNSAFE_KEYS.has(template.path)) {
      config[template.path] = edited;
    }
    return;
  }
  if (Array.isArray(template) && Array.isArray(edited)) {
    for (const [i, element] of template.entries()) {
      walkExtract(element, edited[i], config);
    }
    return;
  }
  if (
    template !== null &&
    typeof template === "object" &&
    edited !== null &&
    typeof edited === "object"
  ) {
    const tObj = template as Record<string, unknown>;
    const eObj = edited as Record<string, unknown>;
    for (const key of Object.keys(tObj)) {
      walkExtract(tObj[key], eObj[key], config);
    }
    return;
  }
  // Non-binding leaf: nothing to extract.
}

/**
 * Collects canonical flow-tree paths where the edit UI should permit
 * editing. Paths are rooted at the flow's `steps` array by step index
 * so they align with how `FlowEditor` renders nodes.
 *
 * Format: `"steps.<stepIndex>.<...inner>"`, e.g.
 * `"steps.2.actions.0.mode"` or
 * `"steps.1.conditionGroups.conditions.0.channels"`.
 */
export function collectEditableFlowPaths(template: {
  readonly steps: readonly unknown[];
}): ReadonlySet<string> {
  const paths = new Set<string>();
  for (const [stepIndex, step] of template.steps.entries()) {
    walkCollect(step, `steps.${stepIndex}`, paths);
  }
  return paths;
}

function walkCollect(node: unknown, prefix: string, paths: Set<string>): void {
  if (isFieldBinding(node)) {
    paths.add(prefix);
    return;
  }
  if (Array.isArray(node)) {
    for (const [i, item] of node.entries()) {
      walkCollect(item, `${prefix}.${i}`, paths);
    }
    return;
  }
  if (node !== null && typeof node === "object") {
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
      walkCollect(v, `${prefix}.${k}`, paths);
    }
  }
}
